import React, { useEffect, useId, useRef, useState } from 'react';
import { Globe, ImagePlus, MapPin, X, Youtube } from 'lucide-react';
import { collection, deleteDoc, doc, onSnapshot, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { Link } from 'react-router-dom';
import { db, storage } from '../firebase';
import { useAuth } from '../AuthContext';
import { hasArtistProfile, matchesProfessions, profileProfessions } from '../artistProfile';
import { DISCUSSION_TYPES, hasCoordinates, latitudeBounds, withinRadius } from '../locationFeed';
import { youtubePostUrl } from '../youtube';
import { authorLocationFields, useAuthorProfiles } from '../services/postInteractions';
import { displayName, normalizeTimestamp } from '../utils';
import { EmptyState, Spinner } from './UI';
import PostCard from './PostCard';
import LocationAutocomplete, { locationFromProfile } from './LocationAutocomplete';
import FeedLocationControls from './FeedLocationControls';
import ProfessionFilters from './ProfessionFilters';

export default function WorkFeed({ ownerId, allowSharing = true }) {
  const { user, profile } = useAuth();
  const fileInput = useRef();
  const locationPanelId = useId();
  const [posts, setPosts] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [text, setText] = useState('');
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState('');
  const [selectedProfessions, setSelectedProfessions] = useState([]);
  const [radius, setRadius] = useState(user ? 2 : null);
  const [elsewhere, setElsewhere] = useState(false);
  const [otherCenter, setOtherCenter] = useState(null);
  const [customPostLocation, setCustomPostLocation] = useState(false);
  const [otherPostLocation, setOtherPostLocation] = useState(null);
  const [postLocationOpen, setPostLocationOpen] = useState(false);
  const [feedError, setFeedError] = useState('');
  const center = elsewhere ? otherCenter : locationFromProfile(profile || {});
  const postingLocation = customPostLocation ? otherPostLocation : locationFromProfile(profile || {});
  const centerLat = center?.lat;
  const centerLng = center?.lng;
  const workUrl = youtubePostUrl(text);
  const canShare = allowSharing && user && hasArtistProfile(profile);

  useEffect(() => {
    setLoading(true);
    setPosts([]);
    setFeedError('');
    if (!ownerId && radius !== null && !hasCoordinates({ lat: centerLat, lng: centerLng })) {
      setLoading(false);
      return undefined;
    }
    // A latitude range uses Firestore's automatic single-field index. Exact
    // great-circle filtering below also handles poles and the date line.
    const bounds = !ownerId && radius !== null ? latitudeBounds({ lat: centerLat, lng: centerLng }, radius) : null;
    const source = ownerId
      ? query(collection(db, 'posts'), where('userId', '==', ownerId))
      : bounds
        ? query(collection(db, 'posts'), where('lat', '>=', bounds.min), where('lat', '<=', bounds.max))
        : query(collection(db, 'posts'), where('type', 'in', DISCUSSION_TYPES));
    return onSnapshot(source, (snapshot) => {
      setPosts(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })).filter((item) => DISCUSSION_TYPES.includes(item.type)).sort((a, b) => normalizeTimestamp(b.createdAt) - normalizeTimestamp(a.createdAt)));
      setFeedError('');
      setLoading(false);
    }, (err) => { setPosts([]); setFeedError(err.message || 'Unable to load discussions.'); setLoading(false); });
  }, [ownerId, radius, centerLat, centerLng]);

  useEffect(() => user?.uid ? onSnapshot(collection(db, 'users', user.uid, 'blockedUsers'), (snapshot) => setBlocked(snapshot.docs.map((item) => item.id)), (err) => setError(err.message || 'Unable to load your block list.')) : undefined, [user?.uid]);
  useEffect(() => {
    if (!image) { setPreview(''); return undefined; }
    const url = URL.createObjectURL(image);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const attachImage = (file) => {
    if (!file || busy) return;
    if (!file.type.startsWith('image/') || file.size > 10 * 1024 * 1024) {
      setError('Choose an image up to 10 MB.');
      return;
    }
    setError('');
    setImage(file);
  };

  const share = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    if (workUrl === null) return setError('Only YouTube video links are supported.');
    if (!text.trim()) return setError('Add a few words to start the discussion.');
    if (!hasArtistProfile(profile)) return setError('Complete your profile before posting.');
    if (!hasCoordinates(postingLocation)) return setError('Select a posting location or update your profile location before posting.');
    setBusy(true);
    const postRef = doc(collection(db, 'posts'));
    let imagePath = '';
    let saved = false;
    try {
      let imageUrl = '';
      if (image) {
        imagePath = `artistWorkImages/${user.uid}/${postRef.id}`;
        const imageRef = ref(storage, imagePath);
        await uploadBytes(imageRef, image, { contentType: image.type });
        imageUrl = await getDownloadURL(imageRef);
      }
      await setDoc(postRef, {
        type: 'area-discussion', userId: user.uid, text: text.trim(), workUrl, imageUrl, imagePath,
        authorUsername: displayName(profile), authorPhotoURL: profile.photoURL || '',
        professions: profileProfessions(profile), artCategories: profileProfessions(profile), musicianSubcategories: [],
        ...authorLocationFields(profile),
        lat: postingLocation.lat, lng: postingLocation.lng,
        locationLabel: postingLocation.label || [postingLocation.area, postingLocation.city, postingLocation.country].filter(Boolean).join(', '),
        area: postingLocation.area || '', city: postingLocation.city || '', country: postingLocation.country || '',
        createdAt: serverTimestamp(),
      });
      saved = true;
      setText(''); setImage(null); setPostLocationOpen(false);
      if (fileInput.current) fileInput.current.value = '';
    } catch (err) {
      setError(err.message || 'Unable to publish your post.');
    } finally {
      if (imagePath && !saved) {
        try { await deleteObject(ref(storage, imagePath)); }
        catch (err) { console.error('Unable to remove an unpublished work image:', err); }
      }
      setBusy(false);
    }
  };

  const remove = async (post) => {
    if (!window.confirm('Delete this post?')) return;
    setDeleting(post.id); setError('');
    try {
      await deleteDoc(doc(db, 'posts', post.id));
      if (post.imagePath) await deleteObject(ref(storage, post.imagePath));
    } catch (err) { setError(err.message || 'Unable to fully delete this post.'); }
    finally { setDeleting(''); }
  };

  const visible = posts.filter((post) => !blocked.includes(post.userId) && (ownerId || withinRadius(post, center, radius)));
  const showFilters = !ownerId;
  const shown = showFilters ? visible.filter((post) => matchesProfessions(post, selectedProfessions)) : visible;
  const authors = useAuthorProfiles(shown.map((post) => post.userId));
  return <section className="work-section">
    {!ownerId && <FeedLocationControls {...{ center, radius, setRadius, elsewhere, setElsewhere }} setCenter={setOtherCenter} />}
    {allowSharing && user && (hasArtistProfile(profile) ? <form className="work-composer" aria-label="Create a post" onSubmit={share}>
      <fieldset disabled={busy}>
        <textarea value={text} maxLength={2000} aria-label="YOUR POST" onChange={(event) => setText(event.target.value)} placeholder="What's happening?" required onPaste={(event) => {
          const file = event.clipboardData?.files?.[0];
          if (file) { event.preventDefault(); attachImage(file); }
        }} />
        {workUrl && <a className="composer-link" href={workUrl} target="_blank" rel="noopener noreferrer"><Youtube size={16} aria-hidden="true" />YouTube video</a>}
        <input ref={fileInput} type="file" accept="image/*" hidden onChange={(event) => {
          attachImage(event.target.files?.[0]);
          event.target.value = '';
        }} />
        {preview && <div className="composer-photo"><img src={preview} alt="Selected photo" /><button type="button" className="icon-button" onClick={() => setImage(null)} aria-label="Remove photo"><X size={16} /></button></div>}
        {postLocationOpen && <div className="composer-location-panel" id={locationPanelId}>
          <LocationAutocomplete value={postingLocation} onChange={(location) => { setOtherPostLocation(location); setCustomPostLocation(true); if (location) setPostLocationOpen(false); }} />
          {customPostLocation && <button type="button" className="text-button" onClick={() => { setCustomPostLocation(false); setPostLocationOpen(false); }}>Use profile location</button>}
        </div>}
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="composer-toolbar">
          <button type="button" className="icon-button" aria-label="Add photo" title="Add photo (up to 10 MB)" onClick={() => fileInput.current?.click()}><ImagePlus size={18} /></button>
          <button type="button" className="composer-location" aria-label="Change posting location" aria-expanded={postLocationOpen} aria-controls={locationPanelId} title={`Post location: ${postingLocation?.label || 'Choose location'}`} onClick={() => setPostLocationOpen(!postLocationOpen)}><MapPin size={15} aria-hidden="true" /><span>{postingLocation?.area || postingLocation?.city || postingLocation?.label || 'Choose location'}</span></button>
          <span className="composer-visibility" role="img" aria-label="Public post and location" title="Public post and location"><Globe size={14} aria-hidden="true" /></span>
          <button className="pill-button" aria-label="Post discussion" disabled={busy}>{busy ? 'Posting...' : 'Post'}</button>
        </div>
      </fieldset>
    </form> : <EmptyState title="Complete your profile" action={<Link className="secondary-button" to="/create-profile">Complete your profile</Link>} />)}
    {error && !canShare && <p className="form-error" role="alert">{error}</p>}
    {showFilters && <ProfessionFilters selected={selectedProfessions} onChange={setSelectedProfessions} />}
    {feedError && <p className="form-error" role="alert">{feedError}</p>}
    {loading ? <Spinner label="Gathering discussions" /> : feedError ? null : shown.length ? <div className="work-feed">{shown.map((post) => <PostCard key={post.id} post={post} author={authors[post.userId]} onDelete={remove} deleting={!!deleting} center={!ownerId ? center : null} />)}</div> : <EmptyState title={selectedProfessions.length ? 'No matching posts' : 'No discussions here yet'} action={!ownerId && <button type="button" className="secondary-button" onClick={() => { setRadius(null); setSelectedProfessions([]); }}>Explore Global</button>} />}
  </section>;
}
